import {cloneElement, useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import PrimaryButton from '../../../ui/PrimaryButton';
import {ActionButtonItem, borderColors, splitMenuItemsIntoSections} from '../../../ui/ActionButton';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import InfoAreaDrawer from '../../../uiControls/infoArea/InfoAreaDrawer';
import constants from '../../../constants';
import {NavigationType, openNewPage} from '../../../util/navigationHelpers';
import {Search} from "../../../filtering";
import {commonCompaniesCompanyList, TableColumn, TableSettings} from '../../../tableSettings';
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../common/AbstractListView";
import JobListViewContext, { RightClickContext } from '../../common/JobListViewContext';

import {
  DateRenderer,DateTimeRenderer,FloatRenderer,IndicatorRenderer,NullableCheckboxIconRenderer,
  NullableCheckboxRedIconRenderer,TextAlignLeftRenderer,TextAlignRightRenderer,TextRenderer,
} from '../../../uiControls/SmartTableRenderers';
import UniqueLinkButton from '../../../uiControls/UniqueLinkButton';
import {CompanyListPreview} from '../../../ifmsTypes';
import {JobNames} from '../../../framework.core/JobNames';
import IfmsModal from "../../../ui/IfmsModal";
import ListViewShortcutMenu from '../ListViewShortcutMenu';
import { CodeCompanyListItem } from '../../../fms.data/models/CodeCompanyListItem';
import * as XCol from '../../../framework.core/XExportColumn';
import ApplicationContext from '../../../ApplicationContext';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import { getFmsGlobal } from '../../../globals';
import { appendLine, findSecurityTask } from '../../../util';
import { FmsSubMenu } from '../../../constants/fmsSubMenu';
import { getSelectedSubMenuArgs } from '../../JobListSubMenu';
import useReportViewer from '../../../hooks/useReportViewer';
import { TaxIdTypeFilterRenderer } from '../commonListRenderers';
import { FmsSecTask } from '../../../fms.core/constants';
import CustomerBalance from '../../reports/AccountBalance';
import { nowDate } from '../../../dateUtils';
import { Decimalformat, nullableCheckBoxValue ,TaxIdType} from '../commonExportFormat';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import Menu,{ IfmsMenuItem } from '../../../ui/menu/Menu';


// function getTaxIdType(t: string) {
//   switch (t) {
//     case 'E': return 'EIN - IRS Number';
//     case 'C': return 'CBP - CBP Assigned Number';
//     case 'S': return 'SSN - Social Security Number';
//     case 'O': return 'OC - Organization Code';
//     case 'U': return 'USCI - United Social Credit Identifier';
//     case 'R': return 'EORI - Economic Operator Reg. ID';
//   }
//   return '';
// }

function TaxIdTypeRenderer({cell}:any) {
  let value = '';

  if (cell.value === 'E') {
    value = 'EIN - IRS Number';
  } else if (cell.value === 'C') {
    value = 'CBP - CBP Assigned Number';
  } else if (cell.value === 'S') {
    value = 'SSN - Social Security Number';
  } else if (cell.value === 'O') {
    value = 'OC - Organization Code';
  } else if (cell.value === 'U') {
    value = 'USCI - United Social Credit Identifier';
  } else if (cell.value === 'R') {
    value = 'EORI - Economic Operator Reg. ID';
  }

  return (
    <div className="ifms-table-align-left" data-cell-actual-value={cell.value}>{value}</div>
  );
}

// function formatElement(data:any) {
//   return {
//     ...data,
//     idType: getTaxIdType(data.idType),
//   };
// }

function formatData(data:Array<CompanyListPreview>) {
  // return data.map(d => formatElement(d));
  return data.map(d => ({
    ...d,
  }));
}

function useSelectedCompanyOperations() {
  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);
  const history = useHistory();

  const selectedElement = jobListContext.selectedElement as CompanyListPreview;

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  
  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl('/common/companies/add')) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    openNewPage(history, '/common/companies/add', NavigationType.newPage);
  }

  async function deleteCompany() {
    let errorMsg = '';

    if (!selectedElement) {
      return;
    }

    if (
      jobListContext.hasOpenedScreenStartingWithUrl(`/common/companies/edit/${selectedElement.id}`) || 
      jobListContext.hasOpenedScreenStartingWithUrl(`/common/companies/view/${selectedElement.id}`)
    ) {
      await showGlobalOkDialog('Need to close Customer first', false, null, "warning");
      return;
    }

    const fmsGlobal = getFmsGlobal();

    let canDelete = await appContext.api.getCheckCompanyIsUsed({ id: selectedElement.id });
    if (canDelete?.result?.item1)
      errorMsg = appendLine(errorMsg, 'Could not check you can delete or not');
    if (canDelete?.result?.item2 || canDelete?.result?.item3)
      errorMsg = appendLine(errorMsg, 'This company is already used and cannot be deleted');

    if (errorMsg) {
      await showGlobalOkDialog(errorMsg);
      return false;
    }

    if (fmsGlobal.CanUseHybrid) {
      canDelete = await appContext.api.getCheckCompanyIsUsedIl3(selectedElement.id, fmsGlobal.FmsMOption?.serversOption?.il3LicID)
      if (canDelete?.result)
        errorMsg = appendLine(errorMsg, 'This company is already used and cannot be deleted');
    }

    if (errorMsg) {
      await showGlobalOkDialog(errorMsg);
      return false;
    }

    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Confirm to Delete?</h3>
      </div>
    , false, 0, true, 'warning');
    if (doDelete) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.Company, { id: selectedElement.id, canUseHybrid: fmsGlobal.CanUseHybrid, pimsID: fmsGlobal.PimsID, il3LicID: fmsGlobal.FmsMOption?.serversOption?.il3LicID });
      if (apiResult && apiResult.success) {
        jobListContext.reloadData();
      } else {
        const message = apiResult?.message ?? 'Cannot delete!';
        await showGlobalOkDialog(message);
      }
    }
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }
    const id = selectedElement.id;
    const editUrl = `/common/companies/edit/${id}`;
    const viewUrl = `/common/companies/view/${id}`;

    // if (jobListContext.hasOpenedScreenStartingWithUrlWithoutMe('/common/companies/edit', editUrl)) {
    //   await showGlobalOkDialog(<p>Edit operation is already in progress</p>);
    //   return;
    // }

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/common/companies/edit/${id}`;
    const viewUrl = `/common/companies/view/${id}`;
    const listSec = findSecurityTask(appContext, FmsSecTask.CodeCompany);
    const urls = [viewUrl];
    if (listSec?.grantEdit) urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  async function copy() {
    if (!selectedElement) {
      return;
    }
  
    if (jobListContext.hasOpenedScreenStartingWithUrl('/common/companies/add')) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }
  
    const id = selectedElement.id;
    openNewPage(history, `/common/companies/add?copyId=${id}`, NavigationType.newPage);
  }

  return { create, deleteCompany, edit, view, copy };
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { create,deleteCompany, edit, view, copy } = useSelectedCompanyOperations();
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const selectedElement = jobListContext.selectedElement as CodeCompanyListItem;
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeCompany);

  const items = [
    { label: 'New', shortcut: 'F2', separator: false, hidden: !listSec?.grantAdd },
    { label: 'View', shortcut: 'F9', separator: !listSec?.grantEdit },
    { label: 'Edit', shortcut: 'F3', separator: !listSec?.grantDel, hidden: !listSec?.grantEdit },
    { label: 'Delete', shortcut: '', separator: true, hidden: !listSec?.grantDel, },
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: constants.Doc_Server, shortcut: 'F8', separator: true, newCol: true },
    { label: 'Copy', shortcut: 'Ctrl+F2', separator: true, hidden: !listSec?.grantAdd },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="company-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
    hidden: i?.hidden ?? false,
  }));

   function onMenuItemSelected(item:IfmsMenuItem) {
      switch (item.index) {
        case 0:
          create();
          break;
        case 1:
          view();
          break;
        case 2:
          edit();
          break;
        case 3:
          deleteCompany();
          break;
        case 4:
          jobListContext.triggerTableSearch('F5');
          break;
        case 5:
          jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
          break;
        case 6:
          copy();
          break;
        case 7:  
          jobListContext.reloadData();
          break;
        default:
          break;
      }
      dismiss();
    }

  
   return (
      <Menu
        variant="plain"
        specialMenu='rightClickContext'
        items={splitMenuItemsIntoSections(menuItems)}
        mode="vertical"
        onMenuItemSelected={onMenuItemSelected}
        subMenuStyle={{
          minWidth: 150,
          whiteSpace: 'nowrap',
        }}
        menuStyle={(i: number) => ({
          maxHeight: '300px',
          overflowY: 'hidden',
          padding: 0,
          borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
          paddingTop: 10,
        })}
        dismiss={dismiss}
      />
    );
}

function CompanyListCommands() {
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const { viewReport, viewEmptyReport } = useReportViewer();
  const appContext = useContext(ApplicationContext);

  function hasOpenedViewOrEditFor(id:number) {
    return jobListContext.hasOpenedScreenStartingWithUrl(`/common/companies/edit/${id}`) || jobListContext.hasOpenedScreenStartingWithUrl(`/common/companies/view/${id}`);
  }

  function hasOpenedNew() {
    return jobListContext.hasOpenedScreenStartingWithUrl(`/common/companies/add`);
  }


  const jobListContext = useContext(JobListViewContext);
  const [selectedPopupMenuItem, setSelectedPopupMenuItem] = useState<ActionButtonItem|null>(null);

  const selectedElement = jobListContext.selectedElement as CompanyListPreview;

  const { edit, view, deleteCompany, copy } = useSelectedCompanyOperations();
  
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeCompany);

  const hasSelectedRow = !!selectedElement;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;
  const canBeCopied = hasSelectedRow && !hasOpenedNew() && listSec?.grantAdd;
  const canBeAdd = !hasOpenedNew() && listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;

  const onPopupClose = () => {
    setSelectedPopupMenuItem(null);
  }

  async function onSubMenuClick(fmsSubMenu: FmsSubMenu) {
    setSelectedPopupMenuItem(null);

    if (!selectedElement) {
      return;
    }

    switch(fmsSubMenu){
      case FmsSubMenu.SendEmail:
        case FmsSubMenu.SendFax:
        viewEmptyReport(fmsSubMenu === FmsSubMenu.SendFax, selectedElement.id.toString());
      break;
    }

    const args = await getSelectedSubMenuArgs(fmsSubMenu, JobNames.Company, selectedElement, appContext, showGlobalYesNoDialog, null);
    if(args){
      viewReport(args, "", true);
    }
  }

  return (
    <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}>
      <div className="ifms-common-no-vertical-separator">
        <UniqueLinkButton name='company-list-add-button' display="primary" size="regular" link="/common/companies/add" disabled={!canBeAdd} label="New" />&nbsp;
        <PrimaryButton name='company-list-view-button' size="regular" disabled={!hasSelectedRow} onClick={view}>View</PrimaryButton>&nbsp;
        <PrimaryButton name='company-list-edit-button' size="regular" disabled={!canBeEdit} onClick={edit}>Edit</PrimaryButton>&nbsp;
        <PrimaryButton name='company-list-delete-button' size="regular" disabled={!canBeDeleted} onClick={deleteCompany}>Delete</PrimaryButton>&nbsp;
        <PrimaryButton name='company-list-copy-button' size="regular" disabled={!canBeCopied} onClick={copy}>Copy</PrimaryButton>
      </div>
      <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}>
        <PrimaryButton
          size="regular"
          style={{ paddingLeft: '5px', width: '40px' }}
          onClick={() => {
            setSelectedPopupMenuItem({
              popupLabel: 'Balance',
              popupWidth: 780,
              label: 'Balance',
              popupComponent:
                <CustomerBalance
                  jobId={selectedElement.id}
                  jobName={JobNames.Company}
                  onPopupClose={() => setSelectedPopupMenuItem(null)}
                  //toDate={invoice.postDate || ''}
                  toDate={nowDate()}
                  comId={selectedElement.id}
                  comType={'C'}
                  branch={''}
                  subCode={''}
                  customerName={selectedElement.shortName ?? ''} />
            }
            )
          }}>
          Bal
        </PrimaryButton>
      </div>
      {/* <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}>
        <ActionButton
          type="primary"
          variant='plain'
          onItemSelected={(event) => setSelectedPopupMenuItem(event)}
          items={[
            { label: 'Agent Statement', separator: false, popupComponent: <AgentStatementDetails tbId={selectedElement?.id.toString()} customerName={selectedElement?.shortName??''} /> },
            { label: 'Customer Statement', separator: false, popupComponent: <CustomerVendorDetails tbId={selectedElement?.id.toString()} customerName={selectedElement?.shortName??''} periodType={''}/> },
            { label: 'PrePickup Order', separator: true, popupComponent: <PickupDelivery jobName={JobNames.Company} docType={DocumentType.PrePickup} selectedElementId={selectedElement?.id} />  },
            { label: 'Print Customer Profile', separator: true, newCol: true, subMenu: FmsSubMenu.CompanyProfile },

            { label: 'Email', separator: false, subMenu: FmsSubMenu.SendEmail },
            { label: 'Fax', separator: true, subMenu: FmsSubMenu.SendFax  },

            { label: 'Importer/Bond Query', separator: false, popupComponent: <ImporterBondQuery selectedElement={selectedElement} />, popupWidth: 550 },
            { label: 'Last Transaction', separator: false, popupComponent: <LastTransaction selectedElement={selectedElement} />},
            { label: 'Memo List (Customer)', separator: false, popupComponent: <MemoList selectedElement={selectedElement} jobName={JobNames.Company} isCompany={true} />  },
          ]}
        >
          More Menu
        </ActionButton>
      </div> */}
      {!!selectedPopupMenuItem && !!selectedPopupMenuItem.popupComponent ? (
        <IfmsModal
          isBlueHeader
          cancelOnEsc
          onCancel={onPopupClose}
          draggable
          isOpen={!!selectedPopupMenuItem}
          title={selectedPopupMenuItem?.popupLabel || selectedPopupMenuItem?.label}
          width={`${selectedPopupMenuItem?.popupWidth || 760}px`}>
          {cloneElement(selectedPopupMenuItem.popupComponent!, { onPopupClose })}
        </IfmsModal>
      ) : null}
      {!!selectedPopupMenuItem && !!selectedPopupMenuItem.subMenu ? onSubMenuClick(selectedPopupMenuItem.subMenu) : null}
    </div>
  );
}

function CompanyListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function CompanyListViewExtras({
  hideLogHistory
}: any,
){
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeCompany);

  const { create, edit, view, copy } = useSelectedCompanyOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string|null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F2") {
      if(listSec?.grantAdd)
        create();
    } else if (unprocessedFkey === "CTRL+F2") {
      if(listSec?.grantAdd)
        copy();
    } else if (unprocessedFkey === "F3") {
      if(listSec?.grantEdit)
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F8") {
      jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey:string) {
    if (['F2', 'F3', 'CTRL+F2', 'F9', 'F11', 'F8'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <InfoAreaDrawer
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.Company}
      jobId={(jobListContext.selectedElement as CompanyListPreview|null)?.id ?? 0}
      onClose={() => jobListContext.setInfoAreaContent('none')}
      hideLogHistory={hideLogHistory}
    />
  );
}

/**
 * Concrete implementation for Company List screen
 *
 * @class CompanyListView
 * @extends {AbstractListView}
 */
 class CompanyListView extends AbstractListView {

  async fetchAndFormatData(api: any, search: Search): Promise<DataFetchResult> {
    //const apiResult = await api.getCommonCompanies(search);
    const apiResult = await api.getListItems(JobNames.Company,
      'CodeCompanyListItem',
      CodeCompanyListItem.calculateSearchParameters(search, CodeCompanyListItem.MapSearchFields),
      1,
      search.pageSize);

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return { data: formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: 'idType',
        renderer: TaxIdTypeFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v,
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.codeCompanyCompany;
  }

  getRowRenderers(): ListRowRenderer[] {
    return CodeCompanyListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if(x.align === XCol.XAlignType.Right) colRenderer.Cell = TextAlignRightRenderer;
      if (x.dataIndex === "status") colRenderer.Cell = IndicatorRenderer;
      if (x.dataIndex === "idType") colRenderer.Cell = TaxIdTypeRenderer;
      if(x.dataIndex === "inactive") colRenderer.Cell = NullableCheckboxRedIconRenderer;
      else if (x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if (x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if (x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if (x.type === XCol.XCellType.Double) {
        if (x.dataIndex === 'terms')
          colRenderer.Cell = (props: any) => <FloatRenderer {...props} decimals={0} />;
        else
          colRenderer.Cell = FloatRenderer;
      }
      return colRenderer;
    });
  }
  
   getExportRenderers(): ExportRowRenderer[] {
     const colRenderer = [];
     colRenderer.push({ accessor: 'status', changedValue: (value: any) => { return value['status'] === 'PC' ? 'Period Close' : '' } });
     colRenderer.push({ accessor: 'inactive', changedValue: (value: any) => { return nullableCheckBoxValue(value['inactive']) } });
     colRenderer.push({ accessor: 'idType', changedValue: (value: any) => { return TaxIdType(value['idType']) } });
     colRenderer.push({ accessor: 'terms', changedValue: (value: any) => { return Decimalformat(value['terms'], 0) } });
     return colRenderer;
   }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'updated',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings = commonCompaniesCompanyList;
    const cols = CodeCompanyListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <CompanyListCommands />
    );
  }

  renderBeforeConfigureButton() {
    return (
      <CompanyListToolbar />
    );
  }

  renderAfterConfigureButton() {
    return null;
  }
  renderExtras(hideLogHistory: boolean) {
    return <CompanyListViewExtras hideLogHistory={hideLogHistory}/>
  }

  getViewUrlFor(row: CompanyListPreview) {
    return row && row.id ? `/common/companies/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Customer List';
  };


  hasRightClickContext(): boolean {
    return true;
  }
   renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
     return <RightClickMenu dismiss={dismiss} />;
   }
  

  useLogHistory(): boolean {
    return true;
  }
};

export default CompanyListView;